import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBuilding, FaChartLine, FaShieldAlt, FaUsers } from "react-icons/fa";
import { useAppTheme } from "../../context/AppThemeContext";
import { getSuperAdminVisitorAnalytics } from "../../api/visitorAnalytics";

const destinations = [
  { title: "Platform dashboard", description: "Manage tenants, subscriptions, discounts, and platform administration.", icon: FaChartLine, to: "/super-admin", tone: "bg-blue-600" },
  { title: "Platform metrics", description: "Review visitor activity, active users, and daily platform signals.", icon: FaShieldAlt, to: "/super-admin/platform-metrics", tone: "bg-teal-600" },
  { title: "Employee management", description: "Create, activate, update, and manage ClassNX employees.", icon: FaUsers, to: "/super-admin/employees", tone: "bg-violet-600" },
  { title: "Tenants", description: "View tenants and open dedicated forms to add or update them.", icon: FaBuilding, to: "/super-admin?panel=tenantPortfolio", tone: "bg-amber-500" },
];

export default function SuperAdminHome() {
  const { isDarkMode } = useAppTheme();
  const [visitorSummary, setVisitorSummary] = useState(null);
  const surface = isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900";
  const muted = isDarkMode ? "text-slate-400" : "text-slate-600";

  useEffect(() => {
    let active = true;
    getSuperAdminVisitorAnalytics(1)
      .then((analytics) => {
        if (active) setVisitorSummary(analytics);
      })
      .catch(() => {
        if (active) setVisitorSummary(null);
      });
    return () => { active = false; };
  }, []);

  return (
    <main className={`min-h-full px-4 py-6 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
      <div className="mx-auto max-w-6xl">
        <section className="overflow-hidden rounded-lg bg-slate-950 px-6 py-8 text-white shadow-xl sm:px-8 sm:py-10">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">ClassNX control center</p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Super Admin home</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">Your starting point for platform administration. Choose a workspace below to continue.</p>
          <Link to="/super-admin" className="mt-6 inline-flex items-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100">Open platform dashboard <FaArrowRight /></Link>
        </section>

        <section className="mt-6 grid gap-4 sm:grid-cols-3" aria-label="Visitor overview">
          <div className={`border p-5 ${surface} rounded-lg`}>
            <p className={`text-xs font-bold uppercase tracking-[0.16em] ${muted}`}>Visitors today</p>
            <p className="mt-2 text-3xl font-black">{visitorSummary ? visitorSummary.todayVisitors.toLocaleString() : "—"}</p>
            <p className={`mt-1 text-sm ${muted}`}>Public ClassNX homepage</p>
          </div>
          <div className={`border p-5 ${surface} rounded-lg`}>
            <p className={`text-xs font-bold uppercase tracking-[0.16em] ${muted}`}>Online now</p>
            <p className="mt-2 text-3xl font-black">{visitorSummary ? visitorSummary.onlineVisitors.toLocaleString() : "—"}</p>
            <p className={`mt-1 text-sm ${muted}`}>Currently active visitors</p>
          </div>
          <Link to="/super-admin/platform-metrics" className={`group border p-5 transition hover:-translate-y-0.5 hover:shadow-lg ${surface} rounded-lg`}>
            <p className={`text-xs font-bold uppercase tracking-[0.16em] ${muted}`}>Visitor analytics</p>
            <p className="mt-2 text-lg font-bold">View the full report</p>
            <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:gap-3">Open metrics <FaArrowRight className="text-xs" /></span>
          </Link>
        </section>

        <section className="mt-6 grid gap-4 sm:grid-cols-2">
          {destinations.map(({ title, description, icon: Icon, to, tone }) => (
            <Link key={title} to={to} className={`group border p-5 transition hover:-translate-y-0.5 hover:shadow-lg ${surface} rounded-lg`}>
              <div className="flex items-start gap-4"><div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white ${tone}`}><Icon /></div><div className="min-w-0 flex-1"><h2 className="text-lg font-bold">{title}</h2><p className={`mt-2 text-sm leading-6 ${muted}`}>{description}</p><span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:gap-3">Open <FaArrowRight className="text-xs" /></span></div></div>
            </Link>
          ))}
        </section>
      </div>
    </main>
  );
}
